/* app/providers/ConfirmProvider.jsx — единый confirm-диалог (// заменяет: window.confirm).
   API: const confirm = useConfirm(); await confirm({title, body, confirmLabel,
        cancelLabel, tone}) → boolean.
   F41: опция checkboxLabel (+checkboxDefault) — чекбокс в теле диалога; при
        подтверждении промис резолвится {ok:true, checked} (truthy — старые
        вызовы `if (!ok)` работают без изменений), при отмене — false.
   Автофокус — на БЕЗОПАСНОЙ кнопке (Отмена), даже для danger (баг авто-сабмита).
   Рендерится через Modal-хром, z-confirm (> modal). */

import React from 'react';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';

  const { createContext, useContext, useState, useCallback, useRef } = React;

  const ConfirmContext = createContext(null);

  function ConfirmProvider({ children }) {
    const [req, setReq] = useState(null);
    const [checked, setChecked] = useState(false);
    const resolver = useRef(null);

    // Разрушительность подтверждения можно объявить двумя способами: tone:'danger'
    // (канон) и danger:true (интуитивный вариант, которым пользуется вся «Академия» —
    // 11 вызовов). Раньше читался только tone, поэтому «Удалить вместе с работами
    // учеников», «Удалить курс» и «Отчислить» рисовались обычной синей кнопкой:
    // самое опасное действие в разделе выглядело как согласие с уведомлением.
    const isDangerTone = (req) => !!req && (req.tone === 'danger' || req.danger === true);

    const confirm = useCallback((options) => {
      return new Promise((resolve) => {
        resolver.current = resolve;
        setChecked(!!(options && options.checkboxDefault));
        setReq({
          title: 'Подтвердите действие',
          body: '',
          confirmLabel: 'Подтвердить',
          cancelLabel: 'Отмена',
          tone: 'accent', // 'danger' для разрушительных
          checkboxLabel: null,
          checkboxDefault: false,
          ...options,
        });
      });
    }, []);

    const close = useCallback((result) => {
      if (resolver.current) { resolver.current(result); resolver.current = null; }
      setReq(null);
    }, []);

    // подтверждение: с чекбоксом — {ok, checked} (truthy), без — true как раньше.
    const acceptRef = useRef(null);
    acceptRef.current = () => {
      if (req && req.checkboxLabel) close({ ok: true, checked });
      else close(true);
    };

    const value = { confirm };

    // Хром — единый <Modal> (role=alertdialog, z-confirm поверх обычных модалок).
    // Автофокус — на БЕЗОПАСНОЙ кнопке (Отмена) даже для danger (баг авто-сабмита).
    return (
      <ConfirmContext.Provider value={value}>
        {children}
        <Modal open={!!req} onClose={() => close(false)} size="sm" role="alertdialog"
          zIndex="var(--z-confirm)" className="modal--confirm" autoFocusSelector="[data-safe]"
          title={req ? req.title : undefined}
          footer={req ? <>
            <Button variant="ghost" data-safe onClick={() => close(false)}>{req.cancelLabel}</Button>
            <Button variant={isDangerTone(req) ? 'danger' : 'primary'} onClick={() => acceptRef.current()}>{req.confirmLabel}</Button>
          </> : undefined}>
          {req && req.body ? <div className="t-body text-dim">{req.body}</div> : null}
          {req && req.checkboxLabel ? (
            <label className="t-body" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)}
                style={{ marginTop: 3 }} />
              <span>{req.checkboxLabel}</span>
            </label>
          ) : null}
        </Modal>
      </ConfirmContext.Provider>
    );
  }

  const useConfirm = () => useContext(ConfirmContext).confirm;

export { ConfirmProvider };
export { useConfirm };
